import axios from 'axios';
import authClient from 'auth/authClient';
import { instalarRefrescoAxios } from 'auth/httpRefresh';

export {
  API_BASE_URL,
  LEVITA_URL,
  MINI_LEVITA_URL,
} from 'constants/ApiConstant';

// Mismo valor que TOKEN_PAYLOAD_KEY en auth/FetchInterceptor.js — se duplica
// en vez de importarse desde ahí porque FetchInterceptor importa el store de
// Redux, y el store termina importando este archivo (vía services/rest/*.js
// -> services/rest/config.js -> utils/axios-request.js), formando un ciclo de
// imports que rompe en dev con "can't access lexical declaration before
// initialization" en el default export de config.js.
const TOKEN_PAYLOAD_KEY = 'authorization';

// --- URLs de backends/servicios externos ---------------------------------
// Todas leen de REACT_APP_* (ver hosting/.env, bloques #PRODUCTION / #DEVELOP)
// en vez de ramificar por NODE_ENV: react-scripts fuerza NODE_ENV=development
// en `npm start` sin importar lo que diga .env, así que un switch en código
// basado en NODE_ENV nunca refleja lo que .env dice localmente. Para cambiar
// de ambiente, comenta/descomenta el bloque correspondiente en .env.

// Backend v2: LEVITA_URL se re-exporta desde constants/ApiConstant.js (arriba).

// Backend nuevo (mini-levita-server), reemplazo de las Cloud Functions.
// Rutas bajo /v1/<NombreDeLaCloudFunction> — ver ../mini-levita-server/src/legacy/ROUTE_MAP.md.
export const MINI_SERVER_URL =
  process.env.REACT_APP_MINI_SERVER_URL || 'https://mini-api.levita.mx';

// Las dos bases de abajo apuntaban a las Cloud Functions por nombre de funcion
// (`<base>/getUserInfo`). El backend nuevo expone esas mismas funciones con el mismo
// nombre bajo `/v1`, asi que apuntarlas ahi no cambia ninguna ruta: solo el host.
//
// Se conservan como variables de entorno a proposito, no se colapsan en una sola: son
// la palanca de reversa. Si algo saliera mal tras el corte, devolverlas a
// `https://us-central1-levita-app.cloudfunctions.net` restaura el comportamiento
// anterior cambiando una variable, sin recompilar ni revertir codigo.
//
// El default ahora es produccion, no el emulador. Antes era
// `http://127.0.0.1:5001/levita-app/us-central1`, de modo que un build de produccion
// que olvidara definirlas dejaba 18 servicios REST apuntando a localhost — y sin fallar
// el build. El valor seguro tiene que ser el default; el emulador se pide explicitamente
// desde `.env.local`, que no se versiona.
export const BASE_URL =
  process.env.REACT_APP_BASE_URL || `${MINI_SERVER_URL}/v1`;

export const FIREBASE_FUNCTIONS_URL =
  process.env.REACT_APP_FIREBASE_FUNCTIONS_URL || `${MINI_SERVER_URL}/v1`;

// Nommy — proveedor externo de nómina.
export const NOMMY_URL =
  process.env.REACT_APP_NOMMY_URL || 'https://dev.app.nommy.mx/api';

// util.nomada.cloud — utilidades internas de Nomada (html-to-pdf, etc.)
export const NOMADA_UTIL_URL =
  process.env.REACT_APP_NOMADA_UTIL_URL || 'https://util.nomada.cloud';

// Genera PDFs de facturas a partir de HTML (llamado desde NOMADA_UTIL_URL).
export const BILLING_API_URL =
  process.env.REACT_APP_BILLING_API_URL || 'https://billing-api.levita.mx';

// OpenReplay — grabación de sesiones.
export const OPENREPLAY_URL =
  process.env.REACT_APP_OPENREPLAY_URL || 'https://or.levita.app';

// Frontends estáticos de encuestas NOM-035.
export const POLITICAS_NOM_URL =
  process.env.REACT_APP_POLITICAS_NOM_URL || 'https://politicas-nom.levita.mx';
export const REPORTES_NOM_URL =
  process.env.REACT_APP_REPORTES_NOM_URL || 'https://reportes-nom.levita.mx';

// Formulario de alta de clientes (Blackswag).
export const BLACKSWAG_URL =
  process.env.REACT_APP_BLACKSWAG_URL || 'https://client.blackswag.io';

// Dominio público de esta misma app — usado para armar deep links (ver
// clientes/empleados desde otras vistas).
export const LEVITA_APP_URL =
  process.env.REACT_APP_LEVITA_APP_URL || 'https://levita.mx';

// AUTH_HEADER pone la CLAVE del header; el VALOR vigente lo repone
// `reponerAuthorization` en cada petición (más abajo). El getter devuelve el
// token del momento en que se lee, pero eso no basta: los 52 sitios que hacen
// `headers: AUTH_HEADER` o `headers: { ...AUTH_HEADER }` lo pasan a
// `axios.create()`, y tanto el spread como el `mergeConfig` de axios COPIAN el
// valor una sola vez, al crear la instancia. Ese valor copiado puede ser un
// token REAL: los módulos de services/rest/*.js entran por `React.lazy`, o sea
// DESPUÉS del login, así que la instancia nace con el token de ese instante y
// se queda con él —vencido a los ~15 min— para el resto de la sesión SPA. (Las
// instancias que sí se crean en el login nacen con `authorization: ''`.) El
// getter se conserva porque hace correcta cualquier lectura directa
// (`AUTH_HEADER.authorization` en un fetch, por ejemplo).
export const AUTH_HEADER = {
  get [TOKEN_PAYLOAD_KEY]() {
    return authClient.getAccessToken();
  },
};

/**
 * Authorization header for the current session. Returns an empty object when
 * nobody is logged in so callers can spread it into fetch/axios headers
 * (HeadersInit does not accept null values).
 * @returns {Record<string, string>}
 */
export const getToken = () => {
  const token = authClient.getAccessToken();
  return token ? { [TOKEN_PAYLOAD_KEY]: token } : {};
};

// --- Auth automático hacia MINI_SERVER_URL -------------------------------
// Varios endpoints portados a mini-levita-server se "endurecieron" (el
// backend viejo no pedía auth, el nuevo sí — ver ROUTE_MAP.md de ese repo) y
// se fueron encontrando, uno por uno, call sites en hosting/ que se habían
// quedado sin mandar el header. En vez de seguir parchando caso por caso
// (y quedar expuestos a que un endpoint nuevo se endurezca mañana y nadie se
// acuerde de actualizar su caller), cualquier request cuya URL final apunte a
// MINI_SERVER_URL manda el header de auth automáticamente si no lo trae ya.
// Mandarlo de más no rompe nada — un endpoint público simplemente lo ignora.
// Solo cuenta como "ya trae auth" una cabecera con VALOR real: una clave
// `authorization` vacía o nula (la que dejó `axios.create()` al copiar
// AUTH_HEADER antes del login) no cuenta, porque axios la conserva hasta el
// interceptor y la descarta al enviar — la request salía sin header
// ("Missing Authorization header" → 401 → logout). Esta función solo AÑADE la
// clave cuando falta; reponer el valor de una clave que ya está es trabajo de
// `reponerAuthorization`.
function hasAuthHeader(headers) {
  if (!headers) return false;
  return Object.keys(headers).some(
    (key) =>
      key.toLowerCase() === TOKEN_PAYLOAD_KEY &&
      headers[key] != null &&
      headers[key] !== ''
  );
}

function injectMiniServerAuth(config) {
  const targetUrl =
    config.url && /^https?:\/\//i.test(config.url)
      ? config.url
      : `${config.baseURL || ''}${config.url || ''}`;

  if (targetUrl.startsWith(MINI_SERVER_URL) && !hasAuthHeader(config.headers)) {
    config.headers = { ...config.headers, ...getToken() };
  }

  return config;
}

// --- Token vigente en el header que ya venía -------------------------------
// Repone el VALOR del header de sesión justo antes de enviar, para cualquier
// host (no solo MINI_SERVER_URL, que es lo único que cubre la función de
// arriba). Es la contraparte de AUTH_HEADER: la clave la puso quien creó la
// instancia, con el token —o la falta de token— de ese instante; aquí se pone
// el de ahora, que es lo único que el server va a aceptar.
//
// Dos reglas, en este orden:
//  - si no hay clave `authorization`, NO se añade: los PUT prefirmados de S3
//    salen por axios y no llevan sesión (firmarlos dos veces los rompe);
//  - si la clave está, se SUSTITUYE siempre por el token en memoria; sin token
//    en memoria el header se deja tal cual venía.
//
// Antes solo se sustituía un valor vacío, con la idea de que un valor "real"
// lo había puesto alguien a propósito. No aplica aquí: los módulos de
// services/rest/*.js entran por `React.lazy`, después del login, así que
// `axios.create()` copia un token real y vigente en la instancia, y a los
// ~15 min esa copia es un token vencido que el interceptor respetaba. Contra
// api-v3 eso cuesta un refresco forzado y un reintento por petición; contra
// mini-api es un 401 seco hasta el siguiente F5. En este árbol no hay ningún
// `Authorization` que no sea el de sesión (el único es el texto de ejemplo de
// views/app-views/components/data-entry/upload/Basic.js, que va por antd
// Upload y no pasa por axios), así que sustituir siempre es seguro.
//
// La búsqueda de la clave es insensible a mayúsculas y conserva la clave tal
// como venía.
function reponerAuthorization(config) {
  const headers = config.headers || {};
  const clave = Object.keys(headers).find((key) => {
    return key.toLowerCase() === TOKEN_PAYLOAD_KEY;
  });
  if (!clave) return config;

  const token = authClient.getAccessToken();
  if (token) config.headers = { ...headers, [clave]: token };

  return config;
}

// Cubre `import axios from 'axios'; axios.get(...)` usado directo en
// vistas/slices (donde han vivido casi todos los casos encontrados).
axios.interceptors.request.use(injectMiniServerAuth);
axios.interceptors.request.use(reponerAuthorization);

// --- Refresco y códigos de sesión ------------------------------------------
// Esta capa (AxiosRequest + la instancia default de axios) es la que usa casi
// todo services/rest/*.js. Ante 401 TOKEN_EXPIRED refresca una vez y reintenta;
// SESSION_*, BLACKLISTED y SESSION_UNAVAILABLE llegan a authClient, que emite
// el evento que cierra la sesión (utils/auth-session.js). Un 401 sin código de
// sesión ya no cierra nada: es un error normal de esa petición (spec fase 3 §6.1).
instalarRefrescoAxios(axios);

export default class AxiosRequest {
  constructor(config = {}, timeout = 60000) {
    this.signal = undefined;
    this.controller = undefined;
    this.instance = axios.create({
      timeout: timeout * 5,
      ...config,
    });
    // `axios.create()` arranca sin los interceptors del default export — cada
    // instancia (incluida la de este wrapper, usada por casi todo
    // services/rest/*.js) necesita el suyo propio. No afecta la instancia
    // pública sin auth de financing-public.js, que no usa esta clase.
    this.instance.interceptors.request.use(injectMiniServerAuth);
    this.instance.interceptors.request.use(reponerAuthorization);
    instalarRefrescoAxios(this.instance);
  }

  getInstance() {
    return {
      signal: this.abort(),
      instance: this.instance,
    };
  }

  getSignal() {
    return this.signal ? { signal: this.signal } : {};
  }

  setCancelToken() {
    const controller = new AbortController();
    const { signal } = controller;
    this.controller = controller;
    this.signal = signal;
  }

  cleanCancelToken() {
    this.controller = undefined;
    this.signal = undefined;
  }

  cancel() {
    if (this?.controller && this?.controller?.abort) {
      this.controller.abort();
    }
  }

  abort() {
    this.cancel();
    this.cleanCancelToken();
    this.setCancelToken();
    return this.getSignal();
  }
}
